// Piezas compartidas entre paneles: avisos de notificaciones/instalación, bandeja de notificaciones, fotos y pagos.

import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { collection, doc, limit, orderBy, query, updateDoc } from "firebase/firestore";
import { hace, pesos, type Notificacion, type Pedido, type Saldo } from "@porteria/core";
import { db, llamar } from "./firebase";
import { fecha, useConsulta, type ConId } from "./datos";
import { activarPush, esIOS, estadoPush, estaInstalada, useInstalar, type EstadoPush } from "./notificaciones";
import { useSesion } from "./sesion";
import { subirFoto, subirFotoPerfil } from "./fotos";
import { Boton, Campo, Hoja, Tarjeta, Vacio, useAccion } from "./ui";
import { Icono, type NombreIcono } from "./iconos";

/** Pedido en efectivo entregado sin cobrar: se dejó en la puerta con foto o el domiciliario aún no marcó que le pagaron. */
export const pendienteDePago = (p: Pedido) => p.metodoPago === "EFECTIVO" && !p.cobrado && !!p.entregadoCon;

/** Foto de una persona (o un ícono si no tiene). `grande` para comparar la cara en la portería. */
export function Cara(props: { url: string | null | undefined; icono?: NombreIcono; grande?: boolean }) {
  const clase = props.grande ? "foto-cara" : "miniatura";
  return props.url ? (
    <img src={props.url} alt="" className={clase} />
  ) : (
    <div className={"icono-redondo neutro" + (props.grande ? " foto-cara" : "")}>
      <Icono nombre={props.icono ?? "moto"} tamano={props.grande ? 40 : 22} />
    </div>
  );
}

/** Foto de la propia persona (obligatoria para domiciliarios: va en el carné y la ve el vigilante al entregar). */
export function FotoPerfil({ texto }: { texto?: ReactNode }) {
  const { cuenta, perfil } = useSesion();
  const { ejecutar, ocupado } = useAccion();
  const subir = (archivo: File) =>
    ejecutar(
      "foto",
      async () => {
        const fotoUrl = await subirFotoPerfil(cuenta!.uid, archivo);
        return llamar("actualizarFoto", { fotoUrl });
      },
      "Foto guardada",
    );
  return (
    <div className="elemento">
      <Cara url={perfil?.fotoUrl} icono="persona" grande />
      <div className="elemento-cuerpo pila">
        {texto && <small>{texto}</small>}
        <label className="boton boton-secundario" aria-busy={!!ocupado} style={ocupado ? { pointerEvents: "none", opacity: 0.55 } : undefined}>
          {ocupado ? <span className="girando" /> : <Icono nombre="camara" />} {perfil?.fotoUrl ? "Cambiar mi foto" : "Tomar mi foto"}
          <input
            type="file"
            accept="image/*"
            capture="user"
            hidden
            onChange={(e) => {
              const archivo = e.target.files?.[0];
              e.target.value = "";
              if (archivo) subir(archivo);
            }}
          />
        </label>
      </div>
    </div>
  );
}

/** Tarjeta que invita a activar las notificaciones (clave para vigilantes y domiciliarios). */
export function AvisoNotificaciones({ motivo }: { motivo: string }) {
  const { cuenta } = useSesion();
  const [estado, setEstado] = useState<EstadoPush | null>(null);
  const { ejecutar, ocupado } = useAccion();
  const instalar = useInstalar();
  useEffect(() => {
    estadoPush().then(setEstado);
  }, []);
  useEffect(() => {
    // Aunque el permiso ya esté dado, registra este celular (el token puede haber cambiado).
    if (estado === "activas" && cuenta) activarPush(cuenta.uid).catch(() => {});
  }, [estado, cuenta]);

  if (!estado || estado === "sin-soporte" || estado === "activas") return null;
  if (estado === "ios-instalar") {
    return (
      <Tarjeta className="alerta">
        <strong>Para recibir avisos en tu iPhone, agrega la app a tu pantalla de inicio</strong>
        <p className="chico">
          En Safari toca <b>Compartir</b> (el cuadro con la flecha) y luego <b>Agregar a inicio</b>. Después abre la app desde el ícono.
        </p>
      </Tarjeta>
    );
  }
  if (estado === "bloqueadas") {
    return (
      <Tarjeta className="alerta">
        <strong>Las notificaciones están bloqueadas</strong>
        <p className="chico">Actívalas en la configuración del navegador para este sitio. {motivo}</p>
      </Tarjeta>
    );
  }
  return (
    <Tarjeta className="alerta">
      <strong>Activa las notificaciones</strong>
      <p className="chico">{motivo}</p>
      <div className="fila">
        <Boton ocupado={ocupado === "push"} onClick={() => ejecutar("push", () => activarPush(cuenta!.uid)).then((e) => e && setEstado(e))}>
          Activar
        </Boton>
        {instalar.puede && (
          <Boton variante="secundario" onClick={instalar.instalar}>
            Instalar app
          </Boton>
        )}
      </div>
    </Tarjeta>
  );
}

export function AvisoInstalar() {
  const instalar = useInstalar();
  if (estaInstalada()) return null;
  if (instalar.puede) {
    return (
      <Tarjeta>
        <div className="entre">
          <span>Instala la app en tu celular para abrirla más rápido.</span>
          <Boton variante="secundario" onClick={instalar.instalar}>
            Instalar
          </Boton>
        </div>
      </Tarjeta>
    );
  }
  if (esIOS()) {
    return (
      <Tarjeta>
        <p className="chico">
          Para instalarla en tu iPhone: en Safari toca <b>Compartir</b> y luego <b>Agregar a inicio</b>.
        </p>
      </Tarjeta>
    );
  }
  return null;
}

/** Campana con la bandeja de notificaciones de la persona. */
export function Campana() {
  const { cuenta } = useSesion();
  const navegar = useNavigate();
  const [abierta, setAbierta] = useState(false);
  const uid = cuenta?.uid;
  const lista = useConsulta<Notificacion>(uid ? `notif-${uid}` : null, () =>
    query(collection(db, `usuarios/${uid}/notificaciones`), orderBy("creadoEn", "desc"), limit(30)),
  );
  const sinLeer = lista?.filter((n) => !n.leida).length ?? 0;

  const abrir = async (n: Notificacion & { id: string }) => {
    if (!n.leida) updateDoc(doc(db, `usuarios/${uid}/notificaciones/${n.id}`), { leida: true }).catch(() => {});
    setAbierta(false);
    if (n.url) navegar(n.url);
  };

  return (
    <>
      <button className="boton-icono" aria-label={`Notificaciones${sinLeer ? `, ${sinLeer} sin leer` : ""}`} onClick={() => setAbierta(true)}>
        <Icono nombre="campana" />
        {sinLeer > 0 && <span className="globo">{sinLeer}</span>}
      </button>
      <Hoja abierta={abierta} alCerrar={() => setAbierta(false)} titulo="Notificaciones">
        {!lista?.length ? (
          <Vacio icono="campanaApagada" titulo="Sin notificaciones" />
        ) : (
          <div className="lista">
            {lista.map((n) => (
              <button key={n.id} className="tarjeta tocable" style={{ textAlign: "left", font: "inherit", color: "inherit" }} onClick={() => abrir(n)}>
                <div className="entre">
                  <strong>{n.titulo}</strong>
                  {!n.leida && <span className="etiqueta tono-activo">Nueva</span>}
                </div>
                <span className="chico">{n.cuerpo}</span>
                <small>{fecha(n.creadoEn) ? hace(fecha(n.creadoEn)!) : ""}</small>
              </button>
            ))}
          </div>
        )}
      </Hoja>
    </>
  );
}

/** Botón para tomar una foto (cámara trasera) y subirla. Devuelve la URL. */
export function TomarFoto(props: { conjuntoId: string; url: string | null; alCambiar: (url: string | null) => void; texto?: string }) {
  const { ejecutar, ocupado } = useAccion();
  return (
    <div className="fila">
      {props.url && <img src={props.url} alt="Foto tomada" className="miniatura" />}
      <label className="boton boton-secundario" style={{ flex: 1 }} aria-busy={!!ocupado}>
        {ocupado ? <span className="girando" /> : <Icono nombre="camara" />} {props.url ? "Cambiar foto" : (props.texto ?? "Tomar foto")}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={async (e) => {
            const archivo = e.target.files?.[0];
            e.target.value = "";
            if (!archivo) return;
            const url = await ejecutar("foto", () => subirFoto(props.conjuntoId, archivo));
            if (url) props.alCambiar(url);
          }}
        />
      </label>
      {props.url && (
        <Boton variante="texto" type="button" onClick={() => props.alCambiar(null)}>
          Quitar
        </Boton>
      )}
    </div>
  );
}

/** Cuando la persona entra a un panel para el que no tiene el rol aprobado. */
export function SinAcceso({ texto }: { texto?: string }) {
  return (
    <div className="pantalla">
      <main className="contenido">
        <Vacio icono="candado" titulo="No tienes acceso a este panel">
          {texto ?? "Tu cuenta no está habilitada con este rol en el conjunto. Revisa el estado de tu solicitud en el inicio."}
        </Vacio>
        <a className="boton boton-secundario" href="/inicio?elegir">
          Ir al inicio
        </a>
      </main>
    </div>
  );
}

/**
 * Registrar un pago del domiciliario (PAGO_LIQUIDACION). Lo usa quien recibe el efectivo en ese conjunto:
 * la plataforma (plataformaRegistrarPago) o la administración (adminRegistrarPago). El servidor revisa el modo.
 */
export function HojaPago(props: { saldo: ConId<Saldo> | null; funcion: "plataformaRegistrarPago" | "adminRegistrarPago"; alCerrar: () => void }) {
  const { saldo, alCerrar } = props;
  const { ejecutar, ocupado } = useAccion();
  const [monto, setMonto] = useState("");
  const [nota, setNota] = useState("Pago en efectivo");
  const [sentido, setSentido] = useState<"recibi" | "pague">("recibi");
  const registrar = async () => {
    const valor = Math.round(Number(monto));
    const r = await ejecutar(
      "pagar",
      () =>
        llamar(props.funcion, {
          // La ruta es conjuntos/{cid}/saldos/{uid}: sirve igual en la consulta de grupo de la plataforma.
          conjuntoId: saldo!.ruta.split("/")[1],
          domiciliarioUid: saldo!.domiciliarioUid ?? saldo!.id,
          monto: sentido === "recibi" ? valor : -valor,
          nota: nota.trim(),
        }),
      "Pago registrado",
    );
    if (r) {
      setMonto("");
      alCerrar();
    }
  };
  return (
    <Hoja abierta={!!saldo} alCerrar={alCerrar} titulo={`Pago de ${saldo?.nombre ?? ""}`}>
      <div className="pila">
        {saldo && <p>{saldo.saldo < 0 ? `Debe ${pesos(-saldo.saldo)} en comisiones.` : saldo.saldo > 0 ? `Saldo a favor: ${pesos(saldo.saldo)}.` : "Está al día."}</p>}
        <div className="opciones">
          <button type="button" className={"opcion" + (sentido === "recibi" ? " elegida" : "")} onClick={() => setSentido("recibi")}>
            Recibí su pago
          </button>
          <button type="button" className={"opcion" + (sentido === "pague" ? " elegida" : "")} onClick={() => setSentido("pague")}>
            Le pagué yo
          </button>
        </div>
        <Campo etiqueta="Monto">
          <input type="number" inputMode="numeric" min={1} max={10_000_000} value={monto} onChange={(e) => setMonto(e.target.value)} />
        </Campo>
        {saldo && saldo.saldo < 0 && sentido === "recibi" && (
          <Boton variante="texto" onClick={() => setMonto(String(-saldo.saldo))}>
            Usar el total adeudado ({pesos(-saldo.saldo)})
          </Boton>
        )}
        {saldo && saldo.saldo > 0 && sentido === "pague" && (
          <Boton variante="texto" onClick={() => setMonto(String(saldo.saldo))}>
            Usar el saldo a favor ({pesos(saldo.saldo)})
          </Boton>
        )}
        <Campo etiqueta="Nota">
          <input value={nota} onChange={(e) => setNota(e.target.value)} maxLength={200} />
        </Campo>
        <Boton grande ocupado={ocupado === "pagar"} disabled={!(Number(monto) > 0) || Number(monto) > 10_000_000 || nota.trim().length < 3} onClick={registrar}>
          Registrar
        </Boton>
      </div>
    </Hoja>
  );
}

/**
 * Pestaña "Más" de los paneles: abre un menú con el perfil, los avisos, instalar la app, cambiar de perfil (solo con
 * más de uno) y cerrar sesión. Antes llevaba directo al selector de perfil, que casi nadie necesita.
 */
export function PestanaMas() {
  const { membresias, esSuper, salir } = useSesion();
  const navegar = useNavigate();
  const { ejecutar, ocupado } = useAccion();
  const [abierta, setAbierta] = useState(false);
  const perfiles = (membresias ?? []).reduce((n, m) => n + Object.values(m.roles ?? {}).filter((r) => r?.estado === "APROBADO").length, 0);
  const ir = (a: string) => {
    setAbierta(false);
    navegar(a);
  };
  const opcion = (icono: NombreIcono, texto: string, accion: () => void, detalle?: string) => (
    <button type="button" className="opcion opcion-fila" onClick={accion}>
      <span className="icono-redondo">
        <Icono nombre={icono} />
      </span>
      <span className="elemento-cuerpo">
        <span>{texto}</span>
        {detalle && <small>{detalle}</small>}
      </span>
    </button>
  );
  return (
    <>
      <button type="button" className="pestana" onClick={() => setAbierta(true)} aria-haspopup="dialog">
        <span className="pestana-icono" aria-hidden>
          <Icono nombre="menu" tamano={22} />
        </span>
        <span>Más</span>
      </button>
      <Hoja abierta={abierta} alCerrar={() => setAbierta(false)} titulo="Más">
        <div className="pila">
          <div className="lista">
            {opcion("persona", "Mi perfil", () => ir("/perfil"), "Tus datos, tu foto y las notificaciones")}
            {(perfiles > 1 || esSuper) && opcion("personas", "Cambiar de perfil", () => ir("/inicio?elegir"), "Residente, domiciliario, portería…")}
            {opcion("mas", "Unirme a otro conjunto", () => ir("/unirme"))}
          </div>
          <AvisoInstalar />
          <Boton variante="secundario" ocupado={ocupado === "salir"} onClick={() => ejecutar("salir", salir)}>
            <Icono nombre="salir" /> Cerrar sesión
          </Boton>
        </div>
      </Hoja>
    </>
  );
}
